/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { isChannelTypeB2B } from '@msdyn365-commerce/core';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IB2bRequestsTileData } from './b2b-requests-tile.data';
import { IB2bRequestsTileProps } from './b2b-requests-tile.props.autogenerated';
import { B2bRequestsTileLinks } from './components/account-b2b-requests-tile-links';

export interface IB2bRequestsTileViewProps extends IB2bRequestsTileProps<IB2bRequestsTileData> {
    b2bRequestsTile: IModuleProps;
    className: string;
    heading?: React.ReactNode;
    links?: React.ReactNode;
}

/**
 *
 * B2bRequestsTile component.
 * @extends {React.PureComponent<IB2bRequestsTileProps<IB2bRequestsTileData>>}
 */
class B2bRequestsTile extends React.PureComponent<IB2bRequestsTileProps<IB2bRequestsTileData>> {
    private readonly telemetryContent?: ITelemetryContent;

    constructor(props: IB2bRequestsTileProps<IB2bRequestsTileData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public render(): JSX.Element | null {
        const { config } = this.props;
        const { className, heading, links } = config;

        const defaultClassName = 'ms-account-b2b-requests-tile';

        if (!isChannelTypeB2B(this.props.context.actionContext.requestContext)) {
            return null;
        }

        const viewProps = {
            ...this.props,
            className,
            b2bRequestsTile: {
                moduleProps: this.props,
                className: classnames(defaultClassName, config.className)
            },
            heading: heading && heading.text && (
                <Msdyn365.Text
                    className={`${defaultClassName}__heading`}
                    tag={heading.tag}
                    text={heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            links: ArrayExtensions.hasElements(links) && (
                <B2bRequestsTileLinks
                    links={links}
                    onTextChange={this.handleLinkTextChange}
                    requestContext={this.props.context.request}
                    telemetryContent={this.telemetryContent}
                />
            )
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading.text = event.target.value);

    public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
        if (this.props.config.links && this.props.config.links[linkIndex]) {
            this.props.config.links[linkIndex].linkText = event.target.value;
        }
    };
}

export default B2bRequestsTile;
